import routes from '../../config/routes';

type RouteLike = {
  path?: string;
  component?: string;
  routes?: RouteLike[];
};

export const NO_MENU_PATH = '/user/no-menu';

const flattenPageRoutePaths = (items: RouteLike[]): Set<string> => {
  const paths = new Set<string>();

  const visit = (routeItems: RouteLike[]) => {
    routeItems.forEach((item) => {
      if (item.path && item.component) {
        paths.add(item.path);
      }
      if (item.routes?.length) {
        visit(item.routes);
      }
    });
  };

  visit(items);
  return paths;
};

const pageRoutePaths = flattenPageRoutePaths(routes as RouteLike[]);
const parentMenuChildren: Record<string, string[]> = {
  '/dashboard': [
    '/dashboard',
    '/dashboard/delivery-dashboard',
    '/dashboard/server-v',
  ],
  '/project-table': [
    '/project-table/list',
    '/project-table/developer',
    '/project-table/product',
    '/project-table/account-opening',
    '/project-table/pending-online',
  ],
  '/product-analysis': [
    '/product-analysis/firebase-binding',
  ],
  '/product-analysis/funnel': [
    '/product-analysis/funnel/overview',
    '/product-analysis/funnel/workbench',
    '/product-analysis/funnel/diagnosis',
    '/product-analysis/funnel/cohort',
    '/product-analysis/funnel/path',
    '/product-analysis/funnel/evidence',
    '/product-analysis/funnel/issues',
    '/product-analysis/funnel/snapshot',
  ],
  '/resource-management': [
    '/resource-management/dashboard',
    '/resource-management/machines',
    '/resource-management/nodes',
    '/resource-management/deploy',
    '/resource-management/check',
    '/resource-management/ip-pool',
    '/resource-management/groups',
    '/resource-management/machines/ssh-keys',
    '/resource-management/machines/auto-create',
    '/resource-management/machines/manual-import',
    '/resource-management/machines/pending-deploy',
    '/resource-management/deploy-templates',
    '/resource-management/deploy-tasks',
    '/resource-management/check-center',
    '/resource-management/russia-check-config',
    '/resource-management/block-events',
    '/resource-management/ip-change-tasks',
    '/resource-management/project-groups',
    '/resource-management/permission-groups',
    '/resource-management/audit-logs',
  ],
  '/system': [
    '/system/queue-monitor',
    '/system/version',
    '/system/app-client',
    '/system/tag-enums',
    '/system/user-permissions',
  ],
};

export const isDefinedMenuUser = (user?: API.CurrentUser) =>
  user?.loginMode === 'operation' && user.user_type === 'define';

export const getDefinedMenuPaths = (user?: API.CurrentUser) => {
  const paths = new Set(
    (user?.menus ?? []).filter((path): path is string => !!path),
  );
  Object.entries(parentMenuChildren).forEach(([parent, children]) => {
    if (paths.has(parent)) {
      children.forEach((path) => {
        paths.add(path);
      });
    }
  });
  return paths;
};

export const getFirstAllowedDefinedMenuPath = (user?: API.CurrentUser) => {
  const menus = user?.menus ?? [];
  for (const path of menus) {
    if (pageRoutePaths.has(path)) return path;
    const firstChild = parentMenuChildren[path]?.find((child) =>
      pageRoutePaths.has(child),
    );
    if (firstChild) return firstChild;
  }
  return undefined;
};

export const isAllowedDefinedMenuPath = (
  pathname: string,
  user?: API.CurrentUser,
) => {
  if (!isDefinedMenuUser(user)) {
    return true;
  }
  if (pathname === NO_MENU_PATH) {
    return true;
  }
  return (
    getDefinedMenuPaths(user).has(pathname) && pageRoutePaths.has(pathname)
  );
};
